import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";
import { proxify, proxifySrcSet } from "@/lib/imgProxy";

/**
 * Renders the highest-priority currently-active promo banner for the given
 * slot, or nothing if no banner is scheduled.
 *
 * Slot IDs in use:
 *   home_hero_strip, home_mid_1, home_bottom_cta,
 *   catalog_top, product_detail_mid, cart_pre_checkout
 */
export default function PromoBannerSlot({ slot_id, variant = "default", className = "" }) {
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get(`/promo-banners/${slot_id}`)
      .then((r) => { if (mounted) setBanner(r.data); })
      .catch(() => { if (mounted) setBanner(null); });
    return () => { mounted = false; };
  }, [slot_id]);

  if (!banner) return null;

  const bg = banner.bg_color || "#FF5722";
  const fg = banner.text_color || "#FFFFFF";

  // Compact "strip" variant — looks like top announce bar (single row, tiny pill CTA)
  if (variant === "strip") {
    return (
      <div
        data-testid={`promo-banner-${slot_id}`}
        className={`relative overflow-hidden border-b border-black/5 shadow-sm sm:border-none sm:shadow-none ${className}`}
        style={{ background: bg }}
      >
        {banner.image && (
          <img loading="lazy" decoding="async"
            src={proxify(banner.image, 1024)}
            srcSet={proxifySrcSet(banner.image, [640, 1024, 1280])}
            sizes="100vw"
            alt=""
            onError={(e) => { e.target.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
        )}
        {banner.image && <div className="absolute inset-0 bg-black/30" />}
        <div className="relative z-10 flex flex-row flex-wrap sm:flex-nowrap items-center justify-between gap-3 sm:gap-6 px-4 sm:px-8 py-3.5 sm:py-3 max-w-7xl mx-auto">
          <div className="flex-1 min-w-[200px] text-left">
            {banner.headline && (
              <div
                className="font-bold text-[13px] sm:text-lg tracking-tight leading-tight sm:font-display sm:font-black"
                style={{ color: fg }}
              >
                {banner.headline}
              </div>
            )}
            {banner.sub_text && (
              <div className="hidden sm:block text-xs opacity-90 mt-0.5" style={{ color: fg }}>
                {banner.sub_text}
              </div>
            )}
          </div>
          {banner.cta_label && (
            <Link
              to={banner.cta_url || "#"}
              data-testid={`promo-banner-cta-${slot_id}`}
              className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 h-9 font-bold text-[12px] sm:text-xs whitespace-nowrap transition-opacity hover:opacity-80 flex-shrink-0 ml-auto"
              style={{ background: fg, color: bg }}
            >
              {banner.cta_label} <ArrowRight size={14} className="flex-shrink-0" />
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Default: rounded inset card (cart, product mid, etc.)
  return (
    <div
      data-testid={`promo-banner-${slot_id}`}
      className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-8 md:my-14 ${className}`}
    >
      <div className="relative overflow-hidden rounded-lg" style={{ background: bg, minHeight: "100px" }}>
        {banner.image && (
          <img loading="lazy" decoding="async"
            src={proxify(banner.image, 1280)}
            srcSet={proxifySrcSet(banner.image, [640, 1024, 1280])}
            sizes="(max-width: 768px) 100vw, 1280px"
            alt=""
            onError={(e) => { e.target.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {banner.image && <div className="absolute inset-0 bg-black/30" />}
        <div className="relative z-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-6 px-4 sm:px-8 py-4 sm:py-6">
          <div className="flex-1 min-w-0">
            {banner.headline && (
              <div className="font-display font-black text-base sm:text-2xl tracking-tight leading-tight break-words" style={{ color: fg }}>
                {banner.headline}
              </div>
            )}
            {banner.sub_text && (
              <div className="text-xs sm:text-sm opacity-90 mt-1 break-words" style={{ color: fg }}>
                {banner.sub_text}
              </div>
            )}
          </div>
          {banner.cta_label && (
            <Link
              to={banner.cta_url || "#"}
              data-testid={`promo-banner-cta-${slot_id}`}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-4 sm:px-5 h-10 sm:h-11 font-bold text-xs sm:text-sm whitespace-nowrap transition-opacity hover:opacity-80 self-stretch sm:self-auto"
              style={{ background: fg, color: bg }}
            >
              {banner.cta_label} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
